import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $isTableNode,
  $isTableRowNode,
  TableRowNode,
} from "@lexical/table";
import { $getNearestNodeFromDOMNode, $getNodeByKey, LexicalNode } from "lexical";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./TableCellResizer.css";

// Minimum row height in pixels (ensures at least one line of text is visible with padding)
const MIN_ROW_HEIGHT = 33;
// Detection zone size - how close the mouse must be to the border to trigger resize mode
// Keep this small so the highlight only appears when the cursor shows resize style
const RESIZE_DETECTION_SIZE = 8;
// Visual overlay size - the clickable area for the resize handle (can be larger for easier clicking)
const RESIZE_OVERLAY_SIZE = 12;

type ResizeDirection = "right" | "bottom";

interface LinePosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface BaseResizeInfo {
  direction: ResizeDirection;
  tableElement: HTMLTableElement;
  tableNodeKey: string;
  columnIndex: number;
  rowIndex: number;
  linePosition: LinePosition;
}

interface DragInfo extends BaseResizeInfo {
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  // All column widths for immediate-neighbor resize behavior
  allColumnWidths: number[];
  // Width of the neighbor column (to the right) at drag start
  neighborWidth: number;
}

// Discriminated union for resize state
type ResizeState =
  | { type: 'idle' }
  | { type: 'hovering'; info: BaseResizeInfo }
  | { type: 'dragging'; info: DragInfo };

function TableCellResizer(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const [resizeState, setResizeState] = useState<ResizeState>({ type: 'idle' });
  const isDraggingRef = useRef(false);

  const getTableNodeKey = useCallback(
    (tableElement: HTMLTableElement): string | null => {
      let tableNodeKey: string | null = null;

      // Use editor.read for synchronous state access
      editor.read(() => {
        const firstCell = tableElement.querySelector("th, td");
        if (!firstCell) return;

        const cellNode = $getNearestNodeFromDOMNode(firstCell);
        if (!cellNode) return;

        let current: LexicalNode | null = cellNode;
        while (current) {
          if ($isTableNode(current)) {
            tableNodeKey = current.getKey();
            break;
          }
          current = current.getParent();
        }
      });

      return tableNodeKey;
    },
    [editor]
  );

  const getResizeDirection = useCallback(
    (event: MouseEvent, cell: HTMLTableCellElement): ResizeDirection | null => {
      const rect = cell.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;

      const nearRightEdge = rect.right - x <= RESIZE_DETECTION_SIZE;
      const nearBottomEdge = rect.bottom - y <= RESIZE_DETECTION_SIZE;

      // Prioritize whichever edge is closer
      if (nearRightEdge && nearBottomEdge) {
        const distToRight = rect.right - x;
        const distToBottom = rect.bottom - y;
        return distToRight < distToBottom ? "right" : "bottom";
      }
      if (nearRightEdge) {
        return "right";
      }
      if (nearBottomEdge) {
        return "bottom";
      }
      return null;
    },
    []
  );

  const calculateLinePosition = useCallback(
    (
      direction: ResizeDirection,
      tableElement: HTMLTableElement,
      columnIndex: number,
      rowIndex: number
    ): LinePosition => {
      const tableRect = tableElement.getBoundingClientRect();

      if (direction === "right") {
        const headerRow = tableElement.querySelector("tr");
        if (headerRow) {
          const cells = headerRow.querySelectorAll("th, td");
          if (cells[columnIndex]) {
            const cellRect = cells[columnIndex].getBoundingClientRect();
            // Overlay is centered on the border for easy clicking
            return {
              top: tableRect.top,
              left: cellRect.right - RESIZE_OVERLAY_SIZE / 2,
              width: RESIZE_OVERLAY_SIZE,
              height: tableRect.height,
            };
          }
        }
      } else if (direction === "bottom") {
        const rows = tableElement.querySelectorAll("tr");
        if (rows[rowIndex]) {
          const rowRect = rows[rowIndex].getBoundingClientRect();
          // Overlay is centered on the border for easy clicking
          return {
            top: rowRect.bottom - RESIZE_OVERLAY_SIZE / 2,
            left: tableRect.left,
            width: tableRect.width,
            height: RESIZE_OVERLAY_SIZE,
          };
        }
      }

      return { top: 0, left: 0, width: 0, height: 0 };
    },
    []
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!editor.isEditable() || isDraggingRef.current) {
        return;
      }

      const target = event.target as HTMLElement;
      const cell = target.closest("th, td") as HTMLTableCellElement | null;

      if (!cell) {
        setResizeState({ type: 'idle' });
        return;
      }

      const direction = getResizeDirection(event, cell);
      if (!direction) {
        setResizeState({ type: 'idle' });
        return;
      }

      const tableElement = cell.closest("table") as HTMLTableElement | null;
      if (!tableElement) {
        setResizeState({ type: 'idle' });
        return;
      }

      const row = cell.closest("tr");
      if (!row) {
        setResizeState({ type: 'idle' });
        return;
      }

      const cells = Array.from(row.querySelectorAll("th, td"));
      const columnIndex = cells.indexOf(cell);
      const rows = Array.from(tableElement.querySelectorAll("tr"));
      const rowIndex = rows.indexOf(row);

      const tableNodeKey = getTableNodeKey(tableElement);
      if (!tableNodeKey) {
        setResizeState({ type: 'idle' });
        return;
      }

      const linePosition = calculateLinePosition(direction, tableElement, columnIndex, rowIndex);

      setResizeState({
        type: 'hovering',
        info: {
          direction,
          tableElement,
          tableNodeKey,
          columnIndex,
          rowIndex,
          linePosition,
        },
      });
    },
    [editor, getResizeDirection, calculateLinePosition, getTableNodeKey]
  );

  const startDrag = useCallback(
    (event: MouseEvent) => {
      if (resizeState.type !== 'hovering' || !editor.isEditable()) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      isDraggingRef.current = true;

      const { direction, tableElement, tableNodeKey, columnIndex, rowIndex, linePosition } = resizeState.info;

      const rows = tableElement.querySelectorAll("tr");
      let startWidth = 0;
      let startHeight = 0;
      let neighborWidth = 0;
      const allColumnWidths: number[] = [];

      // Capture ALL column widths for fixed table layout
      const headerRow = rows[0];
      const headerCells = headerRow?.querySelectorAll("th, td");
      if (headerCells) {
        headerCells.forEach((cell, i) => {
          const width = cell.getBoundingClientRect().width;
          allColumnWidths.push(width);
          if (i === columnIndex) {
            startWidth = width;
          }
          if (i === columnIndex + 1) {
            neighborWidth = width;
          }
        });
      }

      // Capture current row height
      if (rows[rowIndex]) {
        startHeight = rows[rowIndex].getBoundingClientRect().height;
      }

      // Set table-layout: fixed and explicit widths for all columns
      tableElement.style.tableLayout = "fixed";
      const cols = tableElement.querySelectorAll("colgroup col");
      allColumnWidths.forEach((width, i) => {
        if (cols[i]) {
          (cols[i] as HTMLElement).style.width = `${width}px`;
        }
      });

      setResizeState({
        type: 'dragging',
        info: {
          direction,
          tableElement,
          tableNodeKey,
          columnIndex,
          rowIndex,
          linePosition,
          startX: event.clientX,
          startY: event.clientY,
          startWidth,
          startHeight,
          allColumnWidths,
          neighborWidth,
        },
      });
    },
    [editor, resizeState]
  );

  const updateDrag = useCallback(
    (event: MouseEvent) => {
      if (resizeState.type !== 'dragging') return;

      event.preventDefault();

      const { info } = resizeState;
      const deltaX = event.clientX - info.startX;
      const deltaY = event.clientY - info.startY;

      // Update visual feedback during drag
      if (info.direction === "right") {
        const cols = info.tableElement.querySelectorAll("colgroup col");
        const hasNeighbor = info.columnIndex + 1 < info.allColumnWidths.length;

        if (hasNeighbor) {
          // Immediate neighbor resize: adjust both columns inversely
          // Clamp so neither column goes below 0
          const maxExpand = info.neighborWidth;
          const maxShrink = info.startWidth;
          const clampedDelta = Math.max(-maxShrink, Math.min(maxExpand, deltaX));

          const newWidth = info.startWidth + clampedDelta;
          const newNeighborWidth = info.neighborWidth - clampedDelta;

          // Update both columns
          if (cols[info.columnIndex]) {
            (cols[info.columnIndex] as HTMLElement).style.width = `${newWidth}px`;
          }
          if (cols[info.columnIndex + 1]) {
            (cols[info.columnIndex + 1] as HTMLElement).style.width = `${newNeighborWidth}px`;
          }
        } else {
          // Last column: just resize it (no neighbor to adjust)
          const newWidth = Math.max(0, info.startWidth + deltaX);
          if (cols[info.columnIndex]) {
            (cols[info.columnIndex] as HTMLElement).style.width = `${newWidth}px`;
          }
        }

        // Update line position (centered on the border, matching hover positioning)
        const headerRow = info.tableElement.querySelector("tr");
        if (headerRow) {
          const cells = headerRow.querySelectorAll("th, td");
          if (cells[info.columnIndex]) {
            const cellRect = cells[info.columnIndex].getBoundingClientRect();
            setResizeState((prev) => {
              if (prev.type !== 'dragging') return prev;
              return {
                ...prev,
                info: {
                  ...prev.info,
                  linePosition: {
                    ...prev.info.linePosition,
                    left: cellRect.right - RESIZE_OVERLAY_SIZE / 2,
                  },
                },
              };
            });
          }
        }
      } else if (info.direction === "bottom") {
        const newHeight = Math.max(MIN_ROW_HEIGHT, info.startHeight + deltaY);

        const row = info.tableElement.querySelectorAll("tr")[info.rowIndex];
        if (row) {
          (row as HTMLElement).style.height = `${newHeight}px`;

          // Update line position (centered on the border, matching hover positioning)
          const rowRect = row.getBoundingClientRect();
          setResizeState((prev) => {
            if (prev.type !== 'dragging') return prev;
            return {
              ...prev,
              info: {
                ...prev.info,
                linePosition: {
                  ...prev.info.linePosition,
                  top: rowRect.bottom - RESIZE_OVERLAY_SIZE / 2,
                },
              },
            };
          });
        }
      }
    },
    [resizeState]
  );

  const endDrag = useCallback(
    (event: MouseEvent) => {
      if (resizeState.type !== 'dragging') return;

      event.preventDefault();
      isDraggingRef.current = false;

      const { info } = resizeState;
      const deltaX = event.clientX - info.startX;
      const deltaY = event.clientY - info.startY;

      // Clear inline styles we set during drag
      info.tableElement.style.tableLayout = "";
      const cols = info.tableElement.querySelectorAll("colgroup col");
      cols.forEach((col) => {
        (col as HTMLElement).style.width = "";
      });

      editor.update(() => {
        const tableNode = $getNodeByKey(info.tableNodeKey);
        if (!$isTableNode(tableNode)) return;

        if (info.direction === "right") {
          const hasNeighbor = info.columnIndex + 1 < info.allColumnWidths.length;
          const newWidths = [...info.allColumnWidths];

          if (hasNeighbor) {
            // Immediate neighbor resize: adjust both columns inversely
            const maxExpand = info.neighborWidth;
            const maxShrink = info.startWidth;
            const clampedDelta = Math.max(-maxShrink, Math.min(maxExpand, deltaX));

            newWidths[info.columnIndex] = info.startWidth + clampedDelta;
            newWidths[info.columnIndex + 1] = info.neighborWidth - clampedDelta;
          } else {
            // Last column: just resize it
            newWidths[info.columnIndex] = Math.max(0, info.startWidth + deltaX);
          }

          tableNode.setColWidths(newWidths);
        } else if (info.direction === "bottom") {
          const newHeight = Math.max(MIN_ROW_HEIGHT, info.startHeight + deltaY);

          const rows = tableNode.getChildren<TableRowNode>();
          if (info.rowIndex < rows.length) {
            const row = rows[info.rowIndex];
            if ($isTableRowNode(row)) {
              row.setHeight(newHeight);
            }
          }
        }
      });

      setResizeState({ type: 'idle' });
    },
    [editor, resizeState]
  );

  // Mouse move listener on editor root
  useEffect(() => {
    const editorRoot = editor.getRootElement();
    if (!editorRoot) return;

    editorRoot.addEventListener("mousemove", handleMouseMove);

    return () => {
      editorRoot.removeEventListener("mousemove", handleMouseMove);
    };
  }, [editor, handleMouseMove]);

  // Global drag listeners
  useEffect(() => {
    if (resizeState.type !== 'dragging') return;

    document.addEventListener("mousemove", updateDrag);
    document.addEventListener("mouseup", endDrag);

    // Add cursor style to body during drag
    const direction = resizeState.info.direction;
    document.body.style.cursor = direction === "right" ? "col-resize" : "row-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", updateDrag);
      document.removeEventListener("mouseup", endDrag);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [resizeState, updateDrag, endDrag]);

  // Render nothing if idle
  if (resizeState.type === 'idle') {
    return null;
  }

  const { direction, linePosition } = resizeState.info;
  const isDragging = resizeState.type === 'dragging';

  return createPortal(
    <div
      className={`table-resize-line ${isDragging ? "dragging" : ""} ${direction === "right" ? "vertical" : "horizontal"}`}
      style={{
        position: "fixed",
        top: linePosition.top,
        left: linePosition.left,
        width: linePosition.width,
        height: linePosition.height,
        cursor: direction === "right" ? "col-resize" : "row-resize",
        zIndex: 1000,
      }}
      onMouseDown={(e) => startDrag(e.nativeEvent)}
      onMouseLeave={() => {
        // Clear hover state when mouse leaves the resize line overlay
        // (unless we're actively dragging)
        if (!isDraggingRef.current) {
          setResizeState({ type: 'idle' });
        }
      }}
    />,
    document.body
  );
}

export default TableCellResizer;
